import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { useDebounce } from '../../hooks/use-debounce'
import {
  searchSourceSupportsDocumentFilters,
  useRecordSearchClick,
  useSearch,
} from '../../hooks/use-search'
import { useAppStore } from '../../stores/app-store'
import { filtersFromParams, readSearchQuery, readSource, readType } from './search-workspace-state'
import { SearchResultsSection, SearchWorkspaceHeader } from './search-workspace-sections'
import { SearchWorkspaceControls } from './search-workspace-controls'
import type { SearchFilters, SearchResult } from '@agentwiki/shared'
import { buildSearchReturnState } from '../../lib/document-return-continuity'
import {
  captureDocumentReturn,
  returnLocationKey,
  useRestoreDocumentReturn,
} from '../../lib/document-return-restoration'
import { documentReadPath } from '../../lib/document-route'

export function SearchWorkspace() {
  const navigate = useNavigate()
  const location = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const recordClick = useRecordSearchClick()

  const parameterQuery = readSearchQuery(searchParams.get('q'))
  const [input, setInput] = useState(parameterQuery)

  useEffect(() => {
    setInput(parameterQuery)
  }, [parameterQuery])
  const query = input.trim()
  const debouncedQuery = useDebounce(query, 250)
  const source = readSource(searchParams.get('source'))
  const type = readType(searchParams.get('type'))
  const supportsFilters = searchSourceSupportsDocumentFilters(source)
  const filters = useMemo(
    () => (supportsFilters ? filtersFromParams(searchParams) : {}),
    [searchParams, supportsFilters],
  )

  const { data, isFetching, isPlaceholderData, isError, error, refetch } = useSearch(
    debouncedQuery,
    {
      source,
      type,
      limit: 25,
      filters,
      facets: supportsFilters,
    },
  )

  const updateParams = (patch: Record<string, string | undefined>, nextTags?: string[]) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    if (nextTags) {
      next.delete('tag')
      for (const tag of nextTags) next.append('tag', tag)
    }
    setSearchParams(next, { replace: true })
  }

  const updateQuery = (value: string) => {
    setInput(value)
    updateParams({ q: value.trim() || undefined })
  }

  const updateFilters = (nextFilters: SearchFilters) => {
    updateParams(
      {
        category: nextFilters.category,
        dateFrom: nextFilters.dateFrom,
        dateTo: nextFilters.dateTo,
      },
      nextFilters.tags ?? [],
    )
  }

  const updateSource = (nextSource: typeof source) => {
    updateParams(
      {
        source: nextSource,
        category: undefined,
        dateFrom: undefined,
        dateTo: undefined,
      },
      [],
    )
  }

  const openResult = (
    result: SearchResult,
    position: number,
    searchId?: string,
    navigation: 'current' | 'new-tab' = 'current',
  ) => {
    if (result.resultType === 'upload') return

    if (searchId && result.documentId) {
      recordClick.mutate({ searchId, documentId: result.documentId, position })
    }
    if (navigation === 'new-tab') return
    const focusId = result.documentId ?? result.id
    const returnState = buildSearchReturnState(searchParams, focusId)
    const exactKey = returnLocationKey(location.pathname, location.search)
    const safeKey = returnLocationKey(returnState.returnTo.pathname, returnState.returnTo.search)
    captureDocumentReturn(exactKey, focusId, scrollRef.current)
    if (safeKey !== exactKey) captureDocumentReturn(safeKey, focusId, scrollRef.current)
    navigate(documentReadPath({ id: focusId, slug: result.slug }), { state: returnState })
  }

  const hasQuery = query.length >= 2
  const requestMatchesInput = hasQuery && query === debouncedQuery
  const activeQuery = requestMatchesInput && data?.query === debouncedQuery && !isPlaceholderData
  const activeError = requestMatchesInput && isError
  const results = activeQuery ? data.results : []
  const searching =
    hasQuery && (!requestMatchesInput || isFetching || (!activeQuery && !activeError))
  useRestoreDocumentReturn({
    locationKey: returnLocationKey(location.pathname, location.search),
    ready: !searching,
    scrollRef,
    fallbackRef: resultsHeadingRef,
  })

  return (
    <div
      ref={scrollRef}
      data-return-scroll-container
      tabIndex={-1}
      className={cn('h-full overflow-y-auto', isDark ? 'bg-surface-0' : 'bg-neutral-50')}
    >
      <SearchWorkspaceHeader isDark={isDark} onBack={() => navigate('/')} />

      <div className="mx-auto flex max-w-5xl flex-col gap-5 px-4 py-6 md:px-6 md:py-8">
        <SearchWorkspaceControls
          isDark={isDark}
          isFetching={searching}
          input={input}
          source={source}
          type={type}
          supportsFilters={supportsFilters}
          facets={data?.facets}
          filters={filters}
          onInputChange={updateQuery}
          onSourceChange={updateSource}
          onTypeChange={(value) => updateParams({ type: value })}
          onFiltersChange={updateFilters}
        />

        <SearchResultsSection
          hasQuery={hasQuery}
          isError={activeError}
          isFetching={searching}
          errorMessage={error instanceof Error ? error.message : ''}
          results={results}
          searchId={activeQuery ? data.searchId : undefined}
          source={source}
          searchType={type}
          isDark={isDark}
          onOpen={openResult}
          onRetry={() => void refetch()}
          headingRef={resultsHeadingRef}
        />
      </div>
    </div>
  )
}
